import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, SlidersHorizontal } from "lucide-react";

import { ConsolePanel, type AreaTool } from "@/search/ConsolePanel.tsx";
import { SchemaErrorToasts } from "@/diagnostics/SchemaErrorToasts.tsx";
import { DetentSheet } from "@/design-system/DetentSheet.tsx";
import { Hint } from "@/design-system/Hint.tsx";
import { ListingCard } from "@/listings/ListingCard.tsx";
import { ListingPopup } from "@/listings/ListingPopup.tsx";
import { MapCanvas, type DraftCircle, type MapHandle } from "@/map/MapCanvas.tsx";
import { MapControls } from "@/map/MapControls.tsx";
import { ReactorPin } from "@/pins/ReactorPin.tsx";
import { ScaleBar } from "@/map/ScaleBar.tsx";
import { RadarMark } from "@/app/RadarMark.tsx";
import { SharedLinkNotice, type SharedLinkMiss } from "@/share/SharedLinkNotice.tsx";
import { ToolDock } from "@/search/ToolDock.tsx";
import { Sheet, SheetContent, SheetTitle } from "@/design-system/primitives/sheet";
import { useIsDesktop } from "@/platform/use-media-query.ts";
import { useSwipe } from "@/platform/use-swipe.ts";
import { useTheme } from "@/theme/use-theme.ts";
import { fetchBikeways } from "@/bikeways/bikeway.api.ts";
import { fetchDistricts } from "@/districts/district.api.ts";
import { fetchListings } from "@/listings/listing.api.ts";
import { applyFilters } from "@/listings/listing.filter.ts";
import { ageInDays } from "@/listings/listing.format.ts";
import { useHiddenListings } from "@/listings/listing.hidden.ts";
import { markSeen, useSeenListings } from "@/listings/listing.seen.ts";
import { useTrackedListings } from "@/listings/listing.tracked.ts";
import {
  onSharedListingChange,
  readSharedListingId,
  syncSharedListing,
} from "@/share/share.url.ts";
import {
  circlePolygon,
  multiPolygonBBox,
  pointInMultiPolygon,
  unionBBox,
  type BBox,
} from "@/map/geo.ts";
import { type IndexedDistrict } from "@/districts/district.model.ts";
import {
  AGE_MAX,
  DEFAULT_STATE,
  PRICE_ANY,
  PRICE_NO_FLOOR,
  SQFT_NONE,
  TRAVEL_ANY,
  loadState,
  saveState,
  type AppState,
} from "@/search/search.state.ts";
import type { BikewayCollection } from "@/bikeways/bikeway.model.ts";
import type { DistrictCollection } from "@/districts/district.model.ts";
import type { Anchor, Listing } from "@housing/shared";

/** The upstream ingest runs hourly; this keeps a tab left open overnight honest
 *  without hammering the API. */
const POLL_MS = 5 * 60_000;
/** How long the edge holds a response (`server/src/index.ts`). Refetching inside
 *  it returns the same bytes, so it is the floor on any extra refresh. */
const EDGE_CACHE_MS = 60_000;

export default function App() {
  const isDesktop = useIsDesktop();
  const theme = useTheme();

  const [state, setState] = useState<AppState>(loadState);
  const [listings, setListings] = useState<Listing[]>([]);
  const [lastIngestAt, setLastIngestAt] = useState<number | null>(null);
  /** Where every listing's travel times are measured to; null if unconfigured. */
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [districtData, setDistrictData] = useState<{
    collection: DistrictCollection;
    indexed: IndexedDistrict[];
  } | null>(null);

  const [bikeways, setBikeways] = useState<BikewayCollection | null>(null);

  const seen = useSeenListings();
  const tracked = useTrackedListings();
  const hidden = useHiddenListings();
  const [selected, setSelected] = useState<Listing | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  /** False until an inbound `?listing=` has been resolved against loaded data. */
  const [linkResolved, setLinkResolved] = useState(false);
  const [linkMiss, setLinkMiss] = useState<SharedLinkMiss | null>(null);
  /** Live height of the mobile tool dock, so the map controls can clear it. */
  const [dockHeight, setDockHeight] = useState(0);

  // Draft (unapplied) area state. Deliberately not persisted: what you're part-way
  // through drawing isn't "what you were looking at".
  const [tool, setTool] = useState<AreaTool>("none");
  const [draftCircle, setDraftCircle] = useState<DraftCircle | null>(null);
  const [draftDistrictIds, setDraftDistrictIds] = useState<string[]>([]);

  const mapHandle = useRef<MapHandle | null>(null);
  const initialCamera = useRef(state.camera).current;

  const patch = useCallback((next: Partial<AppState>) => {
    setState((s) => ({ ...s, ...next }));
  }, []);

  // Touch gestures on the two off-canvas surfaces. Both track the finger so the
  // panel visibly follows the drag rather than snapping at the threshold.
  const drawerSwipe = useSwipe({
    axis: "x",
    allow: ["left"],
    track: true,
    // Modest, now that `touch-pan-y` on the panel reserves horizontal for us.
    // This was raised to 24 to let a curved thumb-arc resolve before the axis
    // was called, but with the browser free to claim the gesture it also meant
    // 24px in which it could — so a swipe from the middle of the panel was lost
    // before we ever committed to it. The reservation does that job better, and
    // waiting less makes the close feel like it used to.
    lockDistance: 12,
    onSwipe: () => setDrawerOpen(false),
  });

  /**
   * patch, but derived from the state at apply time. Toggles must use this:
   * computing `beds` from the render's `state.beds` loses one of two toggles
   * fired in the same tick, since both would read the same pre-update array.
   */
  const update = useCallback((fn: (s: AppState) => Partial<AppState>) => {
    setState((s) => ({ ...s, ...fn(s) }));
  }, []);

  // ── data ────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    let lastLoadAt = 0;

    const load = async () => {
      lastLoadAt = Date.now();
      try {
        const data = await fetchListings();
        if (cancelled) return;
        setListings(data.listings);
        setLastIngestAt(data.lastIngestAt);
        setAnchor(data.anchor);
        setError(null);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    const timer = window.setInterval(() => void load(), POLL_MS);

    /**
     * …and again the moment a hidden tab comes back.
     *
     * The interval alone is not enough to call this live. A browser throttles
     * timers in a background tab and a phone suspends them outright, so a tab
     * you left this morning can come back showing this morning's map and go on
     * showing it for another five minutes — the one moment someone is certain
     * to be looking is the moment the poll is least likely to have just run.
     *
     * Skipped if we fetched within the last minute, which is how long the edge
     * holds a response: flicking between tabs would otherwise re-download bytes
     * that cannot have changed, against a rate limit that is not generous.
     */
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastLoadAt < EDGE_CACHE_MS) return;
      void load();
    };
    document.addEventListener("visibilitychange", onVisible);

    void fetchDistricts()
      .then((d) => !cancelled && setDistrictData(d))
      .catch((err: unknown) => {
        console.warn("districts unavailable — run `aube run districts`", err);
      });

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  /**
   * The bikeway overlay, fetched the first time it is actually wanted rather
   * than at mount: it is the largest file the app loads, and someone who has
   * switched it off shouldn't pay for it. The ref resets on failure so
   * toggling the switch again is a retry, not a dead control.
   */
  const bikewaysRequested = useRef(false);
  useEffect(() => {
    if (!state.showBikeways || bikewaysRequested.current) return;
    bikewaysRequested.current = true;
    void fetchBikeways()
      .then(setBikeways)
      .catch((err: unknown) => {
        bikewaysRequested.current = false;
        console.warn("bikeways unavailable — run `aube run bikeways`", err);
      });
  }, [state.showBikeways]);

  // ── persistence ─────────────────────────────────────────────────────────
  useEffect(() => {
    // Camera changes fire on every pan; coalesce writes into one per idle beat.
    const id = window.setTimeout(() => saveState(state), 250);
    return () => window.clearTimeout(id);
  }, [state]);

  // ── derived ─────────────────────────────────────────────────────────────
  const districtIndex = useMemo(() => {
    const map = new Map<string, IndexedDistrict>();
    for (const d of districtData?.indexed ?? []) map.set(d.id, d);
    return map;
  }, [districtData]);

  /**
   * Hidden ids come out *before* the filter stack, so a hidden listing never
   * shows up in any facet's dropped count — it isn't "over budget", it's gone.
   * The "Show hidden" switch restores them by skipping this step entirely.
   */
  const unhiddenListings = useMemo(
    () => (state.showHidden ? listings : listings.filter((l) => !hidden.has(l.id))),
    [listings, state.showHidden, hidden],
  );

  /** How many of the listings currently in the data are hidden — what the
   *  console's switch reports, so it never counts ids for long-gone units. */
  const hiddenCount = useMemo(
    () => listings.reduce((n, l) => n + (hidden.has(l.id) ? 1 : 0), 0),
    [listings, hidden],
  );

  const result = useMemo(
    () => applyFilters(unhiddenListings, state, districtIndex),
    [unhiddenListings, state, districtIndex],
  );

  /**
   * Listings per district for the picker's counts. Measured against everything
   * that passes the *non-area* filters, so the numbers answer "what would I get
   * if I picked this one" rather than shifting as the selection changes.
   */
  const districtCounts = useMemo(() => {
    const counts = new Map<string, number>();
    const candidates = districtData?.indexed.filter((d) => d.props.set === state.districtSet) ?? [];
    if (candidates.length === 0 || tool !== "districts") return counts;

    const areaFree = applyFilters(
      unhiddenListings,
      { ...state, area: { mode: "all" } },
      districtIndex,
    ).visible;

    for (const l of areaFree) {
      for (const d of candidates) {
        if (l.lon < d.bbox[0] || l.lon > d.bbox[2] || l.lat < d.bbox[1] || l.lat > d.bbox[3]) {
          continue;
        }
        if (pointInMultiPolygon(l.lon, l.lat, d.geometry)) {
          counts.set(d.id, (counts.get(d.id) ?? 0) + 1);
          break;
        }
      }
    }
    return counts;
  }, [unhiddenListings, state, districtIndex, districtData, tool]);

  // ── deep links ──────────────────────────────────────────────────────────

  /**
   * Resolve an inbound `?listing=` once there's data to resolve it against.
   *
   * Matched against every listing rather than the filtered set: the link is
   * about one unit, and whether it opens shouldn't depend on the *recipient's*
   * filters. When those filters would have hidden it we say so instead, which
   * is a thing they can act on.
   */
  useEffect(() => {
    // `error` matters as much as `loading`: the fetch's `finally` clears
    // `loading` on failure too, leaving an empty `listings`. Resolving against
    // that would tell someone their link was "no longer active" — a specific
    // claim, and a false one — and then strip the id out of the address bar,
    // losing the link for good. An unreachable API is not evidence about a
    // listing, so this waits, and the five-minute poll resolves it once the API
    // answers.
    if (linkResolved || loading || error !== null || !mapReady) return;
    setLinkResolved(true);

    const id = readSharedListingId();
    if (id === null) return;

    const match = listings.find((l) => l.id === id);
    if (!match) {
      setLinkMiss("missing");
      syncSharedListing(null);
      return;
    }

    // Camera first, selection second: the popup mounts with the selection and
    // immediately eases itself on screen at whatever zoom it finds, so the
    // arrival has to be a jump that has already landed rather than a fly still
    // in progress. Past the cluster band (15) so the unit is its own pin rather
    // than a number inside a bubble.
    mapHandle.current?.jumpTo([match.lon, match.lat], Math.max(15.5, initialCamera.zoom));
    setSelected(match);
    if (!result.visible.some((l) => l.id === match.id)) setLinkMiss("filtered");
  }, [linkResolved, loading, error, mapReady, listings, result, initialCamera]);

  // Mirror the selection into the address bar. Held back until the inbound link
  // has been read, or this would strip the very param it's waiting on.
  useEffect(() => {
    if (!linkResolved) return;
    syncSharedListing(selected?.id ?? null);
  }, [selected, linkResolved]);

  // …and back the other way, so Back/Forward move through opened listings.
  useEffect(
    () =>
      onSharedListingChange((id) => {
        setSelected(id === null ? null : (listings.find((l) => l.id === id) ?? null));
      }),
    [listings],
  );

  // Opening a listing is what counts as having seen it. Marked on open rather
  // than on close so the pin has already changed by the time the card is out of
  // the way — otherwise the map appears to rewrite itself as you dismiss.
  useEffect(() => {
    if (selected) markSeen(selected.id);
  }, [selected]);

  // Closing the card retires the notice with it — it was about that card.
  useEffect(() => {
    if (!selected) setLinkMiss((m) => (m === "filtered" ? null : m));
  }, [selected]);

  /**
   * Widen the filters until a shared listing is on the map again.
   *
   * Widened rather than reset to defaults: plenty of people browse with a wider
   * net than DEFAULT_STATE casts, so "go back to defaults" could hide the very
   * listing this button exists to reveal.
   */
  const onRevealShared = useCallback(() => {
    setLinkMiss(null);
    patch({
      area: { mode: "all" },
      // The age slider stops at a fortnight and a shared link can be older than
      // that, so this widens to whatever this listing actually needs rather than
      // to the slider's end — otherwise "Show it" would be a button that visibly
      // does nothing on exactly the links most in need of it. The slider sits at
      // its last stop meanwhile, and touching it snaps back into range.
      maxAgeDays: selected
        ? Math.max(AGE_MAX, Math.ceil(ageInDays(selected.postedAt)) + 1)
        : AGE_MAX,
      maxPrice: PRICE_ANY,
      minPrice: PRICE_NO_FLOOR,
      minSqft: SQFT_NONE,
      includeUnknownSqft: true,
      maxTravelMin: TRAVEL_ANY,
      beds: [0, 1, 2, 3],
      includeUnknownBeds: true,
      // A hidden listing is the one case widening the filters can't reach.
      showHidden: true,
    });
  }, [patch, selected]);

  // ── area tool orchestration ─────────────────────────────────────────────

  const onToolChange = useCallback(
    (next: AreaTool) => {
      setSelected(null);

      if (next === "none") {
        setTool("none");
        setDraftCircle(null);
        setDraftDistrictIds([]);
        return;
      }

      // Opening one tool abandons the other's draft — the two area modes are
      // mutually exclusive, so there's never a half-built circle waiting behind
      // a district selection.
      if (next === "radius") {
        setDraftDistrictIds([]);
        setDraftCircle(state.area.mode === "radius" ? { ...state.area } : null);
      } else {
        setDraftCircle(null);
        setDraftDistrictIds(state.area.mode === "districts" ? [...state.area.ids] : []);
      }
      setTool(next);
      // On a phone the drawer covers the map the tool needs; hand off to the dock.
      if (!isDesktop) setDrawerOpen(false);
    },
    [state.area, isDesktop],
  );

  const onApplyArea = useCallback(() => {
    if (tool === "radius" && draftCircle) {
      patch({ area: { mode: "radius", center: draftCircle.center, radiusM: draftCircle.radiusM } });
    } else if (tool === "districts" && draftDistrictIds.length > 0) {
      patch({ area: { mode: "districts", ids: draftDistrictIds } });
    }
    // Applying retires the tool, which is what makes the drawn geometry vanish.
    setTool("none");
    setDraftCircle(null);
    setDraftDistrictIds([]);
  }, [tool, draftCircle, draftDistrictIds, patch]);

  const onClearArea = useCallback(() => {
    patch({ area: { mode: "all" } });
    setTool("none");
    setDraftCircle(null);
    setDraftDistrictIds([]);
  }, [patch]);

  const onResetAll = useCallback(() => {
    setTool("none");
    setDraftCircle(null);
    setDraftDistrictIds([]);
    setSelected(null);
    // Keep where they are — resetting *filters* shouldn't teleport the map.
    setState((s) => ({ ...DEFAULT_STATE, camera: s.camera, panelOpen: s.panelOpen }));
  }, []);

  /** Frame whatever area is currently applied. */
  const onFocusArea = useCallback(() => {
    const area = state.area;
    if (area.mode === "radius") {
      const ring = circlePolygon(area.center, area.radiusM, 32).coordinates[0];
      const box = ring.reduce<BBox>(
        (acc, [lon, lat]) => [
          Math.min(acc[0], lon),
          Math.min(acc[1], lat),
          Math.max(acc[2], lon),
          Math.max(acc[3], lat),
        ],
        [Infinity, Infinity, -Infinity, -Infinity],
      );
      mapHandle.current?.fitBounds([
        [box[0], box[1]],
        [box[2], box[3]],
      ]);
    } else if (area.mode === "districts") {
      const boxes = area.ids
        .map((id) => districtIndex.get(id))
        .filter((d): d is IndexedDistrict => d !== undefined)
        .map((d) => multiPolygonBBox(d.geometry));
      const box = unionBBox(boxes);
      if (box) {
        mapHandle.current?.fitBounds([
          [box[0], box[1]],
          [box[2], box[3]],
        ]);
      }
    }
    if (!isDesktop) setDrawerOpen(false);
  }, [state.area, districtIndex, isDesktop]);

  const onToggleDistrict = useCallback((id: string) => {
    setDraftDistrictIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }, []);

  // The mobile detail sheet covers the lower half of the screen, so lift the
  // listing it describes into the visible band above it.
  useEffect(() => {
    if (!selected || isDesktop) return;
    mapHandle.current?.revealAt([selected.lon, selected.lat], 0.3);
  }, [selected, isDesktop]);

  // Esc backs out of whatever is open, innermost first.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (selected) setSelected(null);
      else if (tool !== "none") onToolChange("none");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, tool, onToolChange]);

  const panelProps = {
    state,
    patch,
    update,
    result,
    lastIngestAt,
    loading,
    tool,
    onToolChange,
    draftCircle,
    onDraftCircleChange: setDraftCircle,
    draftDistrictIds,
    onDraftDistrictsChange: setDraftDistrictIds,
    onToggleDistrict,
    onApplyArea,
    onClearArea,
    onResetAll,
    onFocusArea,
    districts: districtData?.indexed ?? [],
    districtCounts,
    hiddenCount,
    themeMode: theme.mode,
    themeResolved: theme.resolved,
    onCycleTheme: theme.cycle,
  };

  return (
    <div className="relative h-full w-full overflow-hidden bg-ink-950">
      <MapCanvas
        listings={result.visible}
        districts={districtData?.collection ?? null}
        bikeways={bikeways}
        showBikeways={state.showBikeways}
        theme={theme.resolved}
        initialCamera={initialCamera}
        onCameraChange={(camera) => patch({ camera })}
        onReady={() => setMapReady(true)}
        onSelectListing={setSelected}
        seen={seen}
        tracked={tracked}
        selectedId={selected?.id ?? null}
        cluster={state.clusterListings}
        draftCircle={draftCircle}
        radiusToolActive={tool === "radius"}
        onDraftCircleChange={setDraftCircle}
        districtToolSet={tool === "districts" ? state.districtSet : null}
        draftDistrictIds={draftDistrictIds}
        onToggleDistrict={onToggleDistrict}
        handleRef={mapHandle}
      />

      {/* ── desktop: the console floats over the map ─────────────────────── */}
      {isDesktop && state.panelOpen && (
        <aside className="console console-grid absolute left-4 top-4 z-20 flex max-h-[calc(100dvh-2rem)] w-[21rem] flex-col overflow-hidden rounded-console">
          <ConsolePanel {...panelProps} />
        </aside>
      )}

      {isDesktop && !state.panelOpen && (
        <Hint
          label="Show filters"
          detail={`${result.visible.length.toLocaleString()} listings on the map right now.`}
          side="right"
        >
          <button
            type="button"
            onClick={() => patch({ panelOpen: true })}
            aria-label="Show controls"
            className="console absolute left-4 top-4 z-20 flex items-center gap-2 rounded-console px-3 py-2.5 text-chalk transition hover:border-rule-bright active:scale-[0.98]"
          >
            <RadarMark className="size-5" />
            <span className="font-mono text-[11px] tracking-[0.16em]">RADAR</span>
          </button>
        </Hint>
      )}

      {isDesktop && state.panelOpen && (
        <Hint label="Hide filters" detail="Collapse the console to see more map." side="right">
          <button
            type="button"
            onClick={() => patch({ panelOpen: false })}
            aria-label="Collapse controls"
            className="absolute left-[22.25rem] top-4 z-20 grid size-8 place-items-center rounded-lg border border-rule bg-ink-900/85 text-chalk-dim backdrop-blur transition hover:border-rule-bright hover:text-chalk active:scale-95"
          >
            <SlidersHorizontal className="size-3.5" />
          </button>
        </Hint>
      )}

      {/* ── mobile: a top bar + a left drawer ────────────────────────────── */}
      {!isDesktop && (
        <>
          <div
            className="console absolute inset-x-2 top-2 z-20 flex items-center gap-2.5 rounded-console px-2.5 py-2"
            style={{ top: "max(0.5rem, env(safe-area-inset-top))" }}
          >
            {/* The filters button used to live here. It moved to the top of the
                bottom-right control stack so the phone UI is reachable one-handed
                — this bar is now identity and status only. */}
            <RadarMark className="size-6 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[11px] leading-none tracking-[0.18em] text-chalk">
                RADAR
              </p>
              <p className="tnum mt-1 font-mono text-[9px] uppercase tracking-[0.1em] text-chalk-mute">
                {loading ? "syncing…" : `${result.visible.length.toLocaleString()} listings`}
              </p>
            </div>
          </div>

          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetContent
              side="left"
              className="w-[min(21rem,92vw)] border-r border-rule bg-transparent p-0 [&>button]:hidden"
            >
              <SheetTitle className="sr-only">Map filters</SheetTitle>
              <div
                {...drawerSwipe.handlers}
                className="console console-grid flex h-full touch-pan-y flex-col overflow-hidden"
                style={{
                  transform: `translateX(${Math.min(0, drawerSwipe.offset)}px)`,
                  transition: drawerSwipe.dragging ? "none" : "transform 180ms ease-out",
                }}
              >
                <ConsolePanel {...panelProps} onCloseDrawer={() => setDrawerOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>

          {tool !== "none" && (
            <ToolDock
              tool={tool}
              draftCircle={draftCircle}
              onDraftCircleChange={setDraftCircle}
              draftCount={draftDistrictIds.length}
              onApply={onApplyArea}
              onCancel={() => onToolChange("none")}
              onHeightChange={setDockHeight}
            />
          )}
        </>
      )}

      {/* ── listing detail ───────────────────────────────────────────────── */}
      {selected && isDesktop && mapReady && mapHandle.current?.map && (
        <ListingPopup
          map={mapHandle.current.map}
          listing={selected}
          anchor={anchor}
          onClose={() => setSelected(null)}
        />
      )}

      {/* No `animate-in slide-in-from-bottom` on the sheet. That is a CSS
          *animation* on `transform`, and a running animation outranks an inline
          style — so for its whole duration it drove the sheet to translateY(0),
          i.e. full height in the middle of the screen, and the sheet then
          snapped down to its real resting offset the moment the animation
          ended. DetentSheet animates its own entrance from translateY(100%). */}
      {selected && !isDesktop && (
        <DetentSheet resetKey={selected.id} onDismiss={() => setSelected(null)}>
          <ListingCard
            listing={selected}
            anchor={anchor}
            onClose={() => setSelected(null)}
            variant="sheet"
          />
        </DetentSheet>
      )}

      <ReactorPin
        handle={mapHandle}
        ready={mapReady}
        listingSelected={selected !== null}
        onOpen={() => setSelected(null)}
      />

      {/* Map chrome. Sits above the mobile detail sheet's dock area, and out of
          the way of the desktop console. */}
      <ScaleBar
        handle={mapHandle}
        ready={mapReady}
        className="absolute right-3 z-20"
        style={{
          top: isDesktop ? "1rem" : "calc(max(0.5rem, env(safe-area-inset-top)) + 3.75rem)",
        }}
      />

      {/* Map chrome hides entirely behind the mobile detail sheet — that card
          owns the lower half of the screen — and lifts clear of the tool dock,
          whose height is measured rather than guessed. */}
      {!(selected && !isDesktop) && (
        <MapControls
          handle={mapHandle}
          ready={mapReady}
          onOpenFilters={isDesktop ? undefined : () => setDrawerOpen(true)}
          className="absolute right-3 z-20"
          style={{
            bottom: isDesktop
              ? "1rem"
              : dockHeight > 0
                ? `calc(${dockHeight}px + 1.25rem)`
                : "max(1rem, env(safe-area-inset-bottom))",
          }}
        />
      )}

      {linkMiss && (
        <SharedLinkNotice
          miss={linkMiss}
          onShowAnyway={onRevealShared}
          onDismiss={() => setLinkMiss(null)}
        />
      )}

      <SchemaErrorToasts />

      {/* ── status ───────────────────────────────────────────────────────── */}
      {error && (
        <div className="console absolute bottom-4 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2 rounded-console px-3.5 py-2.5 text-[12px] text-chalk">
          <AlertTriangle className="size-4 shrink-0 text-alert" />
          <span>
            Couldn&rsquo;t reach the listings API — <span className="text-chalk-dim">{error}</span>
          </span>
        </div>
      )}

      {loading && listings.length === 0 && !error && (
        <div className="pointer-events-none absolute inset-0 z-30 grid place-items-center">
          <div className="console flex items-center gap-3 rounded-console px-4 py-3">
            <RadarMark className="size-6" />
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-chalk-dim">
              Scanning…
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
